/**
 * Lime focus ring for form controls on this page.
 *
 * Chakra's default focus ring is blue, which clashes with the project's lime accent — every Input,
 * Select, NumberInput and Textarea on a page has to override it. Kept in one object so the page's
 * controls cannot drift apart.
 */
export const limeFocus = {
  _hover: { borderColor: "gray.400" },
  _focus: { borderColor: "lime", boxShadow: "0 0 0 1px var(--chakra-colors-lime)" },
  _focusVisible: { borderColor: "lime", boxShadow: "0 0 0 1px var(--chakra-colors-lime)" },
};

export default limeFocus;

/**
 * The compact Foundation Data table's merged Value+Unit field (metrics-table-2a) — one fixed total width
 * and one fixed unit-segment width, shared by every row shape that renders it: `EditableMetricRow` (read
 * and edit), `TargetGapRow` (the "Not reported" placeholder), and `AddMetricRow`'s inline add form.
 *
 * Fixed, not flexible: these fields used to be `flex="1" maxW={VALUE_FIELD_WIDTH}` inside a row whose
 * trailing content (icon count, an "Add" button, extra inputs) varies — so each box's RENDERED width was
 * `min(space left after that row's trailing content, max)`, which differs row to row and reads as
 * misaligned columns. A true fixed width removes that dependency entirely: every box is the same size no
 * matter what sits next to it.
 */
export const VALUE_FIELD_WIDTH = "320px";
export const UNIT_SEGMENT_WIDTH = "84px";

/**
 * The trailing icon/action area, to the right of the Value field — also fixed, for the same reason as
 * `VALUE_FIELD_WIDTH` above, and just as important: this area's CONTENT differs by row state (a read row
 * shows info + maybe edit; an editing row shows save + cancel + clear/delete; a gap row shows a single
 * "+ Add" button; the add-row form shows "Add" + cancel), so without a fixed width each state's actions
 * cluster is a different size — and because everything is packed flush right (`justify="flex-end"`), a
 * narrower actions cluster leaves MORE room for the Value field before it, shoving that field's left edge
 * (and so its visible content) sideways rather than keeping it in a fixed column.
 *
 * Sized to fit the widest real case — the add-row's "Add" (text + icon) button plus a Cancel icon button —
 * with room to spare for edit mode's three icon buttons (Save/Cancel/Clear-or-Delete) and the gap row's
 * text "+ Add" button.
 *
 * This is applied to a REAL, separate table column (its own `<Td>`/`<Th>`, not a flex child sharing a
 * merged cell with the Value field) — that is what makes it actually fixed. A `<table>` gives every cell in
 * the same column position the same rendered width, so once this column's width is set, it is identical
 * for every row regardless of that row's own content, and the Value column immediately to its left — right-
 * aligned within ITS OWN column via `justify="flex-end"` — then always sits the same fixed distance from
 * the table's right edge. (The earlier attempt packed the icons into the same flex row as the Value field,
 * inside one cell shared via `colSpan` — which doesn't work: that cell's rendered width still varies with
 * its own content, so the Value field's position shifted depending on how many icons happened to follow
 * it.) Every compact row shape must give its trailing icons/buttons their own `<Td w={ACTIONS_SLOT_WIDTH}>`.
 */
export const ACTIONS_SLOT_WIDTH = "120px";

/**
 * Splits an intensity unit like "tCO2e per currency unit" into its base ("tCO2e") and its "per …" tail, so
 * the base fits the narrow unit segment and the tail can sit beside the datapoint name instead of wrapping
 * the segment onto three lines. A unit with no " per " comes back whole with a null tail.
 */
export function splitPerUnit(unit) {
  if (!unit) return { base: unit, per: null };
  const match = String(unit).match(/^(.*?)\s+(per\s+.+)$/i);
  if (!match || !match[1].trim()) return { base: unit, per: null };
  return { base: match[1].trim(), per: match[2].trim() };
}
